import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faExclamation,
  faHourglass
} from "@fortawesome/free-solid-svg-icons";
import PhoneConsultation from "../../assets/icons/Phone_Consultation_icon.svg";
import ConsultationFailed from "../../assets/icons/Phone_consultation_FAILv2.svg";
import { toSentenceCase } from "../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";

/**
 * Renders the timeline for the test progress, displaying steps with their current status (completed, failed, in progress).
 * Each step is displayed with an icon, label, and an optional link.
 * @returns {JSX.Element} - The rendered timeline component with steps.
 */

const getStepStatus = (step, test, hasFilledAssessment) => {
  const {
    test_kit_status,
    errorReason,
    consultation_status,
    calendly_status,
    attempt_type,
    outcome,
    archive_reason,
    assessment_id,
    is_activated,
    bid: bId,
    partial_result
  } = test || {};

  const errorReasonValue =
    test_kit_status === "error" || test_kit_status === "rejected-internal" && !archive_reason
      ? "marked-rejected"
      : archive_reason;

  if (step.name === "assessment") {
    return {
      failed: !bId && !hasFilledAssessment && !assessment_id,
      current: !bId && !hasFilledAssessment,
      complete: bId || (!bId && hasFilledAssessment)
    };
  }

  return {
    failed: step.failed?.(
      test_kit_status,
      errorReason || errorReasonValue,
      is_activated,
      consultation_status,
      calendly_status,
      attempt_type,
      outcome,
      archive_reason,
      partial_result
    ),
    current: step.inProgress?.(
      test_kit_status,
      is_activated,
      errorReason || errorReasonValue,
      consultation_status
    ),
    complete: step.isCompleted?.(
      test_kit_status,
      errorReason || errorReasonValue,
      is_activated,
      consultation_status,
      partial_result
    )
  };
};

const getStepLabel = (step, status, test) => {
  const { bId } = test || {};

  if (step.name === "assessment" && bId) {
    return step.label || "Assessment Completed";
  }

  if (!status.complete && step.label_before) {
    return step.label_before;
  }

  if (
    step.name === "assessment" &&
    !bId &&
    status.failed &&
    step.label_before
  ) {
    return step.label_before;
  }

  return step.label;
};

const getConsultationGraphics = (
  step,
  unconfirmed_result,
  failed,
  complete
) => {
  const isConsultationStep =
    step.label_before === "Consultation Required" ||
    step.label === "Consultation Completed";

  if (!isConsultationStep) return step;

  return {
    ...step,
    graphic:
      unconfirmed_result && failed ? ConsultationFailed : PhoneConsultation,
    label_before:
      unconfirmed_result && failed
        ? "Consultation Failed"
        : "Consultation Required",
    label:
      unconfirmed_result && failed
        ? "Consultation Failed"
        : unconfirmed_result && complete && "Consultation Completed"
  };
};

const getStepLink = (step, status, test, canEditAssessment) => {
  const { id, test_kit_status } = test || {};

  if (!status.current || test_kit_status === "error") return null;
  if (step.name === "assessment" && !canEditAssessment) return null;

  return step.link?.replace("{test_id}", id) || null;
};

const TimelineStep = ({ step, status, test, link }) => {
  const { failed, current, complete } = status;

  const renderIcon = () => {
    if (failed) return <FontAwesomeIcon icon={faExclamation} />;
    if (current) return <FontAwesomeIcon icon={faHourglass} />;
    if (complete) return <FontAwesomeIcon icon={faCheck} />;
  };

  const StepContainer = link ? Link : "div";
  const untestable =
    step.label === "Analysed at Lab" && test?.archive_reason
      ? "error-untestable"
      : "";
  const partial =
    step.label === "Analysed at Lab" && test?.partial_result
      ? "error-partial"
      : "";
  const failedSample =
    step.label === "Sample Taken & Returned" &&
      (test?.status || test?.test_kit_status) === "error" &&
      failed &&
      !test?.archive_reason
      ? "error-untestable"
      : "";

  return (
    <StepContainer
      key={uuidv4()}
      to={link}
      className={`timeline__step
        ${!step.isVisible?.(test?.unconfirmed_result) &&
        "timeline__step--display"}
        ${current && "timeline__step--current"}
        ${untestable && `timeline__step--${untestable}`}
        ${partial && `timeline__step--${partial}`}
        ${failed && "timeline__step--failed"}
        ${failedSample && `timeline__step--error-untestable`}
        ${complete && "timeline__step--complete"}`}
    >
      <div className="timeline__img-container">
        <img src={step.graphic} alt={step.label} className="timeline__img" />
        <div className="timeline__icon">{renderIcon()}</div>
      </div>
      <p className="timeline__title">{toSentenceCase(step.label)}</p>
    </StepContainer>
  );
};

const TimelineItem = ({
  test,
  steps,
  hasFilledAssessment,
  canEditAssessment
}) => {
  const renderStep = step => {
    if (
      step.label_before === "Consultation Required" &&
      !test?.unconfirmed_result
    ) {
      return null;
    }

    const status = getStepStatus(step, test, hasFilledAssessment);
    const updatedStep = getConsultationGraphics(
      step,
      test?.unconfirmed_result,
      status.failed,
      status.complete
    );
    const label = getStepLabel(updatedStep, status, test);
    const link = getStepLink(updatedStep, status, test, canEditAssessment);

    return (
      <TimelineStep
        key={uuidv4()}
        step={{ ...updatedStep, label }}
        status={status}
        test={test}
        link={link}
      />
    );
  };

  return (
    <div className="timeline mt-4 container-fluid">{steps.map(renderStep)}</div>
  );
};

TimelineItem.propTypes = {
  test: PropTypes.object,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      label_before: PropTypes.string,
      failed: PropTypes.func,
      inProgress: PropTypes.func,
      isCompleted: PropTypes.func,
      isVisible: PropTypes.func,
      link: PropTypes.string,
      graphic: PropTypes.string
    })
  ).isRequired,
  hasFilledAssessment: PropTypes.bool,
  canEditAssessment: PropTypes.bool
};

TimelineStep.propTypes = {
  step: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    label_before: PropTypes.string,
    failed: PropTypes.func,
    inProgress: PropTypes.func,
    isCompleted: PropTypes.func,
    isVisible: PropTypes.func,
    link: PropTypes.string,
    graphic: PropTypes.string
  }).isRequired,
  status: PropTypes.shape({
    failed: PropTypes.bool,
    current: PropTypes.bool,
    complete: PropTypes.bool
  }).isRequired,
  test: PropTypes.object,
  link: PropTypes.string
};
export default TimelineItem;
